import { addDays, differenceInDays, format, isValid, startOfDay } from 'date-fns'

import { PetFormData } from '@/features/booking/types/booking.types'
import { ClientClassification } from '@monorepo/functions/src/types/client'
import { HotelPricingConfig, HotelPricingRates } from '@monorepo/functions/src/types/config'
import { HotelReservation } from '@monorepo/functions/src/types/reservations'
import { AdditionalService } from '@monorepo/functions/src/types/services'

import { getPetsPricing } from './PetsPricing'
import { resolvePricingForDate } from './pricing-periods'

export const calculateNights = (dates: { from: Date; to: Date } | null): number => {
    if (!dates || !isValid(dates.from) || !isValid(dates.to)) return 0
    return differenceInDays(startOfDay(dates.to), startOfDay(dates.from))
}

const formatDateLocal = (date: Date): string => format(startOfDay(date), 'yyyy-MM-dd')

export const getStayNightDates = (dates: { from: Date; to: Date } | null): Date[] => {
    if (!dates || !isValid(dates.from) || !isValid(dates.to)) return []

    const nights = calculateNights(dates)
    if (nights === 0) return [startOfDay(dates.from)]

    const nightDates: Date[] = []
    let currentDate = startOfDay(dates.from)
    for (let i = 0; i < nights; i++) {
        nightDates.push(new Date(currentDate))
        currentDate = addDays(currentDate, 1)
    }
    return nightDates
}

// Function to check if a date is in high season
export const isDateInHighSeason = (
    date: Date,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }>,
): boolean => {
    const dateStr = formatDateLocal(date)
    return highSeasonPeriods.some(period => dateStr >= period.startDate && dateStr <= period.endDate)
}

export const calculateHighSeasonIncrement = (
    dates: { from: Date; to: Date } | null,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }>,
    highSeasonIncrease: number,
): number => {
    if (!dates || !isValid(dates.from) || !isValid(dates.to)) return 0

    const nights = calculateNights(dates)
    if (nights === 0) return 0

    let highSeasonNights = 0
    let currentDate = new Date(dates.from)

    for (let i = 0; i < nights; i++) {
        if (isDateInHighSeason(currentDate, highSeasonPeriods)) {
            highSeasonNights++
        }
        currentDate.setDate(currentDate.getDate() + 1)
    }

    return highSeasonNights * highSeasonIncrease
}

export const calculateHighSeasonIncrementWithPeriods = (
    dates: { from: Date; to: Date } | null,
    periods: HotelPricingRates[],
    petsCount: number,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }>,
): number => {
    const nightDates = getStayNightDates(dates)
    return nightDates.reduce((total, nightDate) => {
        if (!isDateInHighSeason(nightDate, highSeasonPeriods)) return total
        const config = resolvePricingForDate(formatDateLocal(nightDate), periods)
        return total + config.highSeasonIncrease * petsCount
    }, 0)
}

export const calculateLodgingTotal = (
    dates: { from: Date; to: Date } | null,
    pets: PetFormData[],
    periods: HotelPricingRates[],
    clientClassification?: ClientClassification,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }> = [],
): number => {
    const nightDates = getStayNightDates(dates)
    if (!nightDates.length) return 0

    if (calculateNights(dates) === 0) {
        const config = resolvePricingForDate(formatDateLocal(nightDates[0]), periods)
        return config.daycarePrice * pets.length
    }

    return nightDates.reduce((total, nightDate) => {
        const config = resolvePricingForDate(formatDateLocal(nightDate), periods)
        let nightPrice = getPetsPricing(pets, config, clientClassification)
        if (isDateInHighSeason(nightDate, highSeasonPeriods)) {
            nightPrice += config.highSeasonIncrease * pets.length
        }
        return total + nightPrice
    }, 0)
}

// Function to check if there are any high season days in the selected dates
export const hasHighSeasonDays = (
    dates: { from: Date; to: Date } | null,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }>,
): boolean => {
    if (!dates || !isValid(dates.from) || !isValid(dates.to)) return false

    const nights = calculateNights(dates)
    if (nights === 0) return false

    let currentDate = new Date(dates.from)

    for (let i = 0; i < nights; i++) {
        if (isDateInHighSeason(currentDate, highSeasonPeriods)) {
            return true
        }
        currentDate.setDate(currentDate.getDate() + 1)
    }

    return false
}

export const calculateReservationServicePrice = (
    service: AdditionalService,
    nights: number,
    config: HotelPricingConfig,
): number => {
    switch (service.type) {
        case 'medication':
            return service.frequency === 'multiple'
                ? config.medication.multiple.price * nights
                : config.medication.once.price * nights
        case 'special_care':
            return config.healing.price * nights
        case 'special_food':
            return config.specialFood.price * nights
        case 'driver':
            return 0
        case 'hairdressing':
            return 0
        default:
            return 0
    }
}

const calculateServiceTotalForStay = (
    service: AdditionalService,
    nightDates: Date[],
    periods: HotelPricingRates[],
): number => {
    if (nightDates.length === 0) return 0

    return nightDates.reduce((total, nightDate) => {
        const config = resolvePricingForDate(formatDateLocal(nightDate), periods)
        return total + calculateReservationServicePrice(service, 1, config)
    }, 0)
}

export interface PetPriceBreakdown {
    basePrice: number
    servicesPrice: number
    subtotal: number
    total: number
    services: Array<{
        name: string
        price: number
    }>
}

export interface TotalPriceBreakdown {
    petsBreakdown: Record<number, PetPriceBreakdown>
    total: number
}

export const calculateTotalReservationPrice = (
    dates: { from: Date; to: Date } | null,
    pets: PetFormData[],
    services: AdditionalService[],
    periods: HotelPricingRates[],
    clientClassification?: ClientClassification,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }> = [],
): TotalPriceBreakdown => {
    const nights = calculateNights(dates)
    const nightDates = getStayNightDates(dates)
    const lodgingTotal = calculateLodgingTotal(dates, pets, periods, clientClassification, highSeasonPeriods)

    const driverService = services.find(s => s.type === 'driver')
    const servicesByPet = pets.map((_, index) => ({
        services: services.filter(s => 'petIndex' in s && s.petIndex === index && s.type !== ('driver' as string)),
    }))

    const petsBreakdown: Record<number, PetPriceBreakdown> = {}
    let totalPrice = 0

    const basePricePerPet = pets.length > 0 ? lodgingTotal / pets.length : 0

    pets.forEach((pet, index) => {
        const petServices = servicesByPet[index].services

        const servicesBreakdown = petServices.map(service => ({
            name: service.type,
            price: calculateServiceTotalForStay(service, nightDates, periods),
        }))

        const servicesPrice = servicesBreakdown.reduce((total, service) => total + service.price, 0)
        const subtotal = basePricePerPet + servicesPrice

        petsBreakdown[index] = {
            basePrice: basePricePerPet,
            servicesPrice,
            subtotal,
            total: subtotal,
            services: servicesBreakdown,
        }

        totalPrice += subtotal
    })

    if (driverService && dates) {
        const checkoutDate = formatDateLocal(nights === 0 ? dates.from : dates.to)
        const checkoutConfig = resolvePricingForDate(checkoutDate, periods)
        totalPrice += calculateReservationServicePrice(driverService, nights, checkoutConfig)
    }

    return {
        petsBreakdown,
        total: totalPrice,
    }
}

export const calculateTotalPrice = (
    reservation: HotelReservation,
    periods: HotelPricingRates[],
    clientClassification?: ClientClassification,
    highSeasonPeriods: Array<{ startDate: string; endDate: string }> = [],
) => {
    const dates = { from: new Date(reservation.checkInDate), to: new Date(reservation.checkOutDate) }
    const nights = calculateNights(dates)
    const nightDates = getStayNightDates(dates)
    const lodgingTotal = calculateLodgingTotal(
        dates,
        reservation.pets,
        periods,
        clientClassification,
        highSeasonPeriods,
    )

    let totalPrice = 0

    totalPrice += reservation.additionalServices.reduce((total, service) => {
        if (service.type === 'driver') {
            return total + (service.price || 0)
        }
        if (service.type === 'hairdressing') {
            return total + (service.price || 0)
        }
        const dailyService = service as { days?: number; pricePerDay?: number; price?: number }
        if (
            typeof dailyService.days === 'number' &&
            typeof dailyService.pricePerDay === 'number' &&
            dailyService.days >= 0 &&
            dailyService.pricePerDay >= 0
        ) {
            return total + dailyService.days * dailyService.pricePerDay
        }
        return total + (nights === 0 ? (service.price || 0) : (service.price || 0) * nights)
    }, 0)
    totalPrice += reservation.extras?.reduce((total, extra) => total + extra.amount, 0) || 0
    totalPrice += reservation.shopProducts?.reduce((total, product) => total + product.totalPrice, 0) || 0
    totalPrice += lodgingTotal

    const petsPriceByDay = nights === 0
        ? lodgingTotal
        : lodgingTotal / Math.max(nights, 1)

    return {
        total: totalPrice,
        petsPriceByDay,
        nights,
    }
}
